import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Send, Eye, Plus, Trash2, ChevronUp, ChevronDown,
  X, Check, Loader2, Paperclip, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import RichTextEditor from '../../components/newsletter/RichTextEditor';

const ALL_ROLES = [
  { value: 'stagiaire', label: 'Stagiaires' },
  { value: 'formateur', label: 'Formateurs' },
  { value: 'tuteur', label: 'Tuteurs' },
  { value: 'entreprise', label: 'Entreprises' },
  { value: 'moniteur_vnm', label: 'Moniteurs' },
  { value: 'responsable_pedagogique', label: 'Resp. Pédagogiques' },
  { value: 'candidat', label: 'Candidats' },
  { value: 'prospect', label: 'Prospects' },
];

// --- Migration: convertit un ancien article (texte brut + image_base64 + youtube_url) en HTML enrichi ---
function legacyArticleToHtml(article) {
  const parts = [];
  if (article.image_base64) {
    parts.push(
      `<img src="${article.image_base64}" alt="" style="max-width:100%;height:auto;display:block;margin:12px auto;border-radius:6px;" />`
    );
  } else if (article.image_url) {
    parts.push(
      `<img src="${article.image_url}" alt="" style="max-width:100%;height:auto;display:block;margin:12px auto;border-radius:6px;" />`
    );
  }
  const raw = (article.text || '').trim();
  if (raw) {
    let t = raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // mini-syntaxe historique: **gras** et [texte](url)
    t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#8DC63F;text-decoration:underline;font-weight:600;">$1</a>');
    t = t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    t = t.replace(/\n/g, '<br/>');
    parts.push(`<p>${t}</p>`);
  }
  const m = (article.youtube_url || '').match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  const ytId = m ? m[1] : '';
  if (ytId) {
    parts.push(
      `<a href="https://www.youtube.com/watch?v=${ytId}" target="_blank" rel="noopener noreferrer" data-type="youtube" data-video-id="${ytId}" style="display:block;text-align:center;text-decoration:none;margin:16px 0;">` +
        `<img src="https://img.youtube.com/vi/${ytId}/hqdefault.jpg" alt="Vidéo YouTube" style="max-width:100%;width:480px;height:auto;display:block;margin:0 auto;border-radius:6px;" />` +
      `</a>`
    );
  }
  return parts.join('');
}


export default function NewsletterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [articles, setArticles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]); // par défaut: aucun rôle sélectionné
  const [extraEmails, setExtraEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [savedContacts, setSavedContacts] = useState([]); // [{contact_id, email, label}]
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [status, setStatus] = useState('draft');
  const [uploadingAttachment, setUploadingAttachment] = useState(null); // article index en cours d'upload, sinon null

  useEffect(() => {
    if (id) fetchNewsletter();
  }, [id]);

  const fetchNewsletter = async () => {
    try {
      const res = await axios.get(`/newsletters/${id}`);
      setTitle(res.data.title);
      const rawArticles = res.data.articles || [];
      // Migration douce : si un ancien article n'a pas de HTML dans `text`,
      // on convertit image_base64 / youtube_url / texte brut en HTML enrichi
      const migrated = rawArticles.map(a => {
        const txt = (a.text || '').trim();
        const looksHtml = /<(p|h\d|ul|ol|a |img|strong|em|u>|br)/i.test(txt);
        if (looksHtml) return a;
        return { ...a, text: legacyArticleToHtml(a) };
      });
      setArticles(migrated);
      setStatus(res.data.status || 'draft');
    } catch (err) {
      toast.error('Newsletter introuvable');
      navigate('/newsletters');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/newsletters/${id}`, { title, articles });
      toast.success('Newsletter sauvegardée');
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleArticleAttachmentUpload = async (articleIndex, e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const currentAtts = articles[articleIndex]?.attachments || [];
    if (currentAtts.length >= 3) {
      toast.error('Maximum 3 pièces jointes par article');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`Fichier trop volumineux : ${(file.size/1024/1024).toFixed(1)} Mo. Maximum 5 Mo.`);
      return;
    }
    setUploadingAttachment(articleIndex);
    try {
      // Save first to ensure article index is persisted in DB
      await axios.put(`/newsletters/${id}`, { title, articles });
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `/newsletters/${id}/articles/${articleIndex}/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setArticles(prev => prev.map((a, i) =>
        i === articleIndex ? { ...a, attachments: [...(a.attachments || []), res.data] } : a
      ));
      toast.success('Pièce jointe ajoutée');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingAttachment(null);
    }
  };

  const handleArticleAttachmentRemove = async (articleIndex, attachmentId) => {
    try {
      await axios.delete(`/newsletters/${id}/articles/${articleIndex}/attachments/${attachmentId}`);
      setArticles(prev => prev.map((a, i) =>
        i === articleIndex ? { ...a, attachments: (a.attachments || []).filter(x => x.attachment_id !== attachmentId) } : a
      ));
      toast.success('Pièce jointe supprimée');
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddArticle = () => {
    setArticles(prev => [...prev, {
      title: '',
      text: '',
      order: prev.length,
      attachments: [],
    }]);
  };

  const updateArticle = (index, field, value) => {
    setArticles(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
  };

  const removeArticle = (index) => {
    setArticles(prev => prev.filter((_, i) => i !== index));
  };

  const moveArticle = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= articles.length) return;
    const newArr = [...articles];
    [newArr[index], newArr[newIndex]] = [newArr[newIndex], newArr[index]];
    setArticles(newArr);
  };

  const handlePreview = async () => {
    await handleSave();
    try {
      const res = await axios.post(`/newsletters/${id}/preview`);
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch (err) {
      toast.error('Erreur aperçu');
    }
  };

  const handleSend = async () => {
    if (selectedRoles.length === 0 && extraEmails.length === 0 && selectedContactIds.length === 0) {
      toast.error('Sélectionnez au moins un destinataire');
      return;
    }
    await handleSave();
    setSending(true);
    try {
      const res = await axios.post(`/newsletters/${id}/send`, {
        roles: selectedRoles,
        extra_emails: extraEmails,
        saved_contact_ids: selectedContactIds,
      });
      toast.success(res.data.message);
      setShowSendDialog(false);
      setStatus('sent');
      // Recharger les contacts (au cas où certains extra_emails viennent d'être sauvegardés)
      loadSavedContacts();
      setExtraEmails([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  const addExtraEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    // Sauvegarde immédiate côté serveur dans le carnet
    try {
      const res = await axios.post('/newsletters/saved-contacts', { email });
      const newContact = res.data;
      // Insère ou met à jour la liste locale
      setSavedContacts(prev => {
        const without = prev.filter(c => c.email !== newContact.email);
        return [...without, newContact].sort((a, b) => a.email.localeCompare(b.email));
      });
      // Auto-sélectionne ce contact pour l'envoi en cours
      setSelectedContactIds(prev =>
        prev.includes(newContact.contact_id) ? prev : [...prev, newContact.contact_id]
      );
      setNewEmail('');
      toast.success('Contact ajouté au carnet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email invalide');
    }
  };

  const removeExtraEmail = (email) => {
    setExtraEmails(prev => prev.filter(e => e !== email));
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const toggleContact = (contactId) => {
    setSelectedContactIds(prev =>
      prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
    );
  };

  const removeSavedContact = async (contactId) => {
    if (!window.confirm('Retirer ce contact du carnet ?')) return;
    try {
      await axios.delete(`/newsletters/saved-contacts/${contactId}`);
      setSavedContacts(prev => prev.filter(c => c.contact_id !== contactId));
      setSelectedContactIds(prev => prev.filter(id => id !== contactId));
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const loadSavedContacts = async () => {
    try {
      const res = await axios.get('/newsletters/saved-contacts');
      setSavedContacts(res.data.contacts || []);
    } catch {
      // silencieux
    }
  };

  useEffect(() => {
    if (showSendDialog) loadSavedContacts();
  }, [showSendDialog]);

  return (
    <div className="max-w-4xl mx-auto" data-testid="newsletter-editor">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/newsletters')}
          className="flex items-center gap-2 text-slate-500 hover:text-[#0F172A] transition-colors"
          data-testid="back-to-list"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" onClick={handlePreview}
            className="gap-2" data-testid="preview-btn"
          >
            <Eye className="h-4 w-4" /> Aperçu
          </Button>
          <Button
            variant="outline" onClick={handleSave} disabled={saving}
            className="gap-2" data-testid="save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Sauvegarder
          </Button>
          <Button
            onClick={() => setShowSendDialog(true)}
            className="bg-[#8DC63F] hover:bg-[#7ab635] text-white gap-2"
            data-testid="send-btn"
          >
            <Send className="h-4 w-4" /> Envoyer
          </Button>
        </div>
      </div>

      {/* Title */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <label className="block text-sm font-medium text-slate-600 mb-2">Titre de la newsletter</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex: Newsletter de Mars 2026"
          className="w-full text-2xl font-bold text-[#0F172A] border-0 border-b-2 border-slate-200 focus:border-[#8DC63F] focus:outline-none pb-2 bg-transparent"
          data-testid="newsletter-title-input"
        />
      </div>

      {/* Articles */}
      <div className="space-y-4 mb-6">
        {articles.map((article, index) => (
          <ArticleCard
            key={index}
            article={article}
            index={index}
            total={articles.length}
            onUpdate={(field, value) => updateArticle(index, field, value)}
            onRemove={() => removeArticle(index)}
            onMove={(dir) => moveArticle(index, dir)}
            onAttachmentUpload={(e) => handleArticleAttachmentUpload(index, e)}
            onAttachmentRemove={(attId) => handleArticleAttachmentRemove(index, attId)}
            isUploadingAttachment={uploadingAttachment === index}
          />
        ))}
      </div>

      {/* Add article button */}
      <button
        onClick={handleAddArticle}
        className="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-400 hover:border-[#8DC63F] hover:text-[#8DC63F] transition-colors flex items-center justify-center gap-2"
        data-testid="add-article-btn"
      >
        <Plus className="h-5 w-5" />
        Ajouter un article
      </button>

      {/* Send Dialog */}
      {showSendDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="send-dialog">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#0F172A]">Envoyer la newsletter</h2>
              <button onClick={() => setShowSendDialog(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* ROLES (groupes) */}
            <p className="text-xs uppercase tracking-wide font-semibold text-slate-400 mb-2">Groupes</p>
            <div className="space-y-1.5 mb-5">
              {ALL_ROLES.map(role => (
                <label
                  key={role.value}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    selectedRoles.includes(role.value)
                      ? 'border-[#8DC63F] bg-[#8DC63F]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`role-${role.value}`}
                >
                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                    selectedRoles.includes(role.value) ? 'bg-[#8DC63F]' : 'border-2 border-slate-300'
                  }`}>
                    {selectedRoles.includes(role.value) && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.value)}
                    onChange={() => toggleRole(role.value)}
                    className="hidden"
                  />
                  <span className="text-sm font-medium text-[#0F172A]">{role.label}</span>
                </label>
              ))}
            </div>

            {/* SAVED CONTACTS (carnet d'adresses) */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wide font-semibold text-slate-400">
                  Contacts enregistrés ({savedContacts.length})
                </p>
                {savedContacts.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedContactIds(
                        selectedContactIds.length === savedContacts.length
                          ? []
                          : savedContacts.map(c => c.contact_id)
                      )
                    }
                    className="text-xs text-[#8DC63F] hover:underline"
                    data-testid="toggle-all-contacts"
                  >
                    {selectedContactIds.length === savedContacts.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                )}
              </div>
              {savedContacts.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Aucun contact enregistré. Ajoutez-en un ci-dessous.</p>
              ) : (
                <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                  {savedContacts.map(c => (
                    <div
                      key={c.contact_id}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded border ${
                        selectedContactIds.includes(c.contact_id)
                          ? 'border-[#8DC63F] bg-[#8DC63F]/5'
                          : 'border-slate-200'
                      }`}
                      data-testid={`saved-contact-${c.contact_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedContactIds.includes(c.contact_id)}
                        onChange={() => toggleContact(c.contact_id)}
                        className="h-4 w-4 accent-[#8DC63F] cursor-pointer"
                      />
                      <span className="text-sm text-slate-700 flex-1 truncate" title={c.email}>{c.email}</span>
                      <button
                        type="button"
                        onClick={() => removeSavedContact(c.contact_id)}
                        className="text-slate-300 hover:text-red-500"
                        title="Retirer du carnet"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ADD NEW EMAIL */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wide font-semibold text-slate-400 mb-2">Ajouter un nouvel email</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExtraEmail())}
                  placeholder="email@exemple.com"
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#8DC63F] focus:ring-1 focus:ring-[#8DC63F]"
                  data-testid="extra-email-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addExtraEmail}
                  className="px-3"
                  data-testid="add-extra-email-btn"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">L&apos;email sera ajouté au carnet et coché automatiquement.</p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSendDialog(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || (selectedRoles.length === 0 && selectedContactIds.length === 0)}
                className="flex-1 bg-[#8DC63F] hover:bg-[#7ab635] text-white gap-2"
                data-testid="confirm-send-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col" data-testid="preview-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-[#0F172A]">Aperçu email</h2>
              <button onClick={() => setShowPreview(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-full min-h-[600px] border-0"
                title="Aperçu newsletter"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ArticleCard({ article, index, total, onUpdate, onRemove, onMove, onAttachmentUpload, onAttachmentRemove, isUploadingAttachment }) {
  const attachmentInputRef = useRef(null);
  const attachments = article.attachments || [];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`article-card-${index}`}>
      {/* Article header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <span className="text-sm font-semibold text-slate-500">Article {index + 1}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMove(-1)} disabled={index === 0}
            className="p-1.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            onClick={() => onMove(1)} disabled={index === total - 1}
            className="p-1.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            onClick={onRemove}
            className="p-1.5 text-slate-400 hover:text-red-500 ml-2"
            data-testid={`remove-article-${index}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Title */}
        <input
          type="text"
          value={article.title}
          onChange={(e) => onUpdate('title', e.target.value)}
          placeholder="Titre de l'article"
          className="w-full text-lg font-semibold text-[#0F172A] border-0 border-b border-slate-200 focus:border-[#8DC63F] focus:outline-none pb-2 bg-transparent"
          data-testid={`article-title-${index}`}
        />

        {/* Rich text body */}
        <RichTextEditor
          value={article.text || ''}
          onChange={(html) => onUpdate('text', html)}
          placeholder="Contenu de l'article…"
          testId={`article-text-${index}`}
        />
        <p className="text-xs text-slate-400 -mt-1">
          Utilisez la barre d&apos;outils pour mettre en forme, insérer une image, un lien, ou une vidéo YouTube.
        </p>

        {/* Pièces jointes de l'article */}
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-[#8DC63F]" />
              <span className="text-sm font-medium text-slate-700">Pièces jointes</span>
              <span className="text-xs text-slate-400">({attachments.length}/3)</span>
            </div>
            {attachments.length < 3 && (
              <button
                type="button"
                onClick={() => attachmentInputRef.current?.click()}
                disabled={isUploadingAttachment}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-[#8DC63F] hover:bg-[#7ab635] disabled:opacity-50 text-white rounded-md transition-colors"
                data-testid={`article-${index}-add-attachment`}
              >
                {isUploadingAttachment ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Ajouter PJ
              </button>
            )}
          </div>
          <input
            ref={attachmentInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,application/pdf,image/jpeg,image/png,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={onAttachmentUpload}
            data-testid={`article-${index}-attachment-input`}
          />
          {attachments.length === 0 ? (
            <p className="text-xs text-slate-400 italic">PDF, image, Word, Excel — 5 Mo max par fichier</p>
          ) : (
            <ul className="space-y-1.5">
              {attachments.map(a => (
                <li
                  key={a.attachment_id}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-slate-50 rounded border border-slate-200"
                  data-testid={`article-${index}-attachment-${a.attachment_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-slate-700 truncate">{a.filename}</span>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      ({(a.size / 1024 / 1024).toFixed(2)} Mo {a.size <= 3 * 1024 * 1024 ? '· joint' : '· lien'})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onAttachmentRemove(a.attachment_id)}
                    className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0"
                    title="Retirer cette pièce jointe"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
